import React, { useState, useEffect } from 'react';
import { Card, Switch, Table, Alert, Button, message, Tag, Typography, Tooltip, Space } from 'antd';
import { SaveOutlined, CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';
import { adminAPI } from '../api/admin';

const { Text, Link } = Typography;

export default function LanguagesSettingsManager() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getLanguagesSettings();
      if (res.data.success) {
        setSettings(res.data.settings || {});
      } else {
        message.error(res.data.message || t('admin.LanguagesSettingsManager.loadError'));
      }
    } catch (error) {
      message.error(t('admin.common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (lang, enabled) => {
    setSettings((prev) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        enabled
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.updateLanguagesSettings({ settings });
      if (res.data.success) {
        message.success(t('admin.LanguagesSettingsManager.saveSuccess'));
        setSettings(res.data.settings || {});
      } else {
        message.error(res.data.message || t('admin.LanguagesSettingsManager.saveError'));
      }
    } catch (error) {
      message.error(t('admin.LanguagesSettingsManager.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('admin.common.copySuccess'));
  };

  const dataSource = Object.entries(settings).map(([code, config]) => ({
    key: code,
    code,
    name: config.name,
    enabled: config.enabled,
    subdomain: config.subdomain,
    domain: config.domain,
    host: config.domain || config.subdomain
  }));

  const columns = [
    {
      title: t('admin.LanguagesSettingsManager.languageColumn'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span>
          <strong>{text}</strong> <Tag color="blue">{record.code}</Tag>
        </span>
      )
    },
    {
      title: t('admin.LanguagesSettingsManager.availabilityColumn'),
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled, record) => {
        // زبان‌های fa و en الزامی هستند و غیرقابل غیرفعال‌سازی
        const isDisabled = record.code === 'fa' || record.code === 'en';
        return (
          <Tooltip title={isDisabled ? t('admin.LanguagesSettingsManager.lockedTooltip') : ''}>
            <Switch
              checked={enabled}
              disabled={isDisabled}
              onChange={(checked) => handleToggle(record.code, checked)}
              checkedChildren={t('admin.LanguagesSettingsManager.enabledSwitch')}
              unCheckedChildren={t('admin.LanguagesSettingsManager.disabledSwitch')}
            />
          </Tooltip>
        );
      }
    },
    {
      title: t('admin.LanguagesSettingsManager.hostColumn'),
      dataIndex: 'host',
      key: 'host',
      render: (host) => host ? (
        <Link href={`https://${host}`} target="_blank">
          {host}
        </Link>
      ) : <Text type="secondary">—</Text>
    },
    {
      title: t('admin.LanguagesSettingsManager.dnsColumn'),
      key: 'dns',
      render: (_, record) => {
        if (!record.enabled) return <Text type="secondary">{t('admin.LanguagesSettingsManager.dnsAfterEnable')}</Text>;

        const ip = '62.60.188.9';
        if (record.domain) {
          return (
            <Space direction="vertical" size={2}>
              <div>
                <Tag color="cyan">A Record</Tag>
                <Text code onClick={() => copyToClipboard(record.domain)} style={{ cursor: 'pointer' }}>
                  {record.domain}
                </Text>
                <Text type="secondary"> → </Text>
                <Text code onClick={() => copyToClipboard(ip)} style={{ cursor: 'pointer' }}>
                  {ip}
                </Text>
              </div>
              <div>
                <Tag color="cyan">A Record</Tag>
                <Text code onClick={() => copyToClipboard(`www.${record.domain}`)} style={{ cursor: 'pointer' }}>
                  www.{record.domain}
                </Text>
                <Text type="secondary"> → </Text>
                <Text code onClick={() => copyToClipboard(ip)} style={{ cursor: 'pointer' }}>
                  {ip}
                </Text>
              </div>
            </Space>
          );
        }

        if (record.subdomain) {
          const prefix = record.subdomain.split('.')[0];
          return (
            <div>
              <Tag color="purple">CNAME / A Record</Tag>
              <Text code onClick={() => copyToClipboard(prefix)} style={{ cursor: 'pointer' }}>
                {prefix}
              </Text>
              <Text type="secondary"> → </Text>
              <Text code onClick={() => copyToClipboard(ip)} style={{ cursor: 'pointer' }}>
                {ip}
              </Text>
              <Tooltip title={t('admin.LanguagesSettingsManager.cnameTooltip')}>
                <QuestionCircleOutlined style={{ marginStart: 6, color: '#1890ff', cursor: 'pointer' }} />
              </Tooltip>
            </div>
          );
        }

        return <Text type="secondary">—</Text>;
      }
    }
  ];

  return (
    <Card
      title={t('admin.LanguagesSettingsManager.title')}
      loading={loading}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          {t('admin.common.saveSettings')}
        </Button>
      }
    >
      <Alert
        message={t('admin.LanguagesSettingsManager.infoTitle')}
        description={
          <div>
            <ul>
              <li><Trans i18nKey="admin.LanguagesSettingsManager.infoBullet1" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="admin.LanguagesSettingsManager.infoBullet2" components={{ strong: <Text strong /> }} /></li>
              <li><Trans i18nKey="admin.LanguagesSettingsManager.infoBullet3" components={{ code: <Text code /> }} /></li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
      />
    </Card>
  );
}
